import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Button,
  ButtonGroup,
  CardSelectionCard,
  CardSelectionGroup,
  Label,
  Link as MUILink,
  Space,
  TextHeading,
} from '@ally/metronome-ui'
import { Account } from '@ally/data-types-afg-accounts'
import { maskAccountNumber } from '@ally/aos-utils'
import { Private } from '@ally/private'
import allytm from '@ally/allytm'
import styled from 'styled-components'

const StyledLabel = styled(Label)`
  display: block;
  margin-bottom: 16px;
`
const StyledSpace = styled(Space)`
  width: 100%;
  @media (min-width: 640px) {
    max-width: 500px;
  }
`
interface AccountSelectorProps {
  accounts: Account[]
  onSubmit: (accountId: string) => void
  identifier?: 'id' | 'accountNumber'
  title?: string
  subTitle?: string
  labelText?: string
  errorMessage?: string
}

const DEFAULT_TITLE = "Let's get you where you need to be."
const DEFAULT_SUBTITLE = ''
const DEFAULT_LABEL_TEXT = 'Select one:'
const DEFAULT_ERROR_MESSAGE = 'Select an account to continue.'
const DEFAULT_IDENTIFIER = 'id'

const AccountSelector: React.FC<AccountSelectorProps> = ({
  accounts,
  onSubmit,
  title = DEFAULT_TITLE,
  subTitle = DEFAULT_SUBTITLE,
  labelText = DEFAULT_LABEL_TEXT,
  errorMessage = DEFAULT_ERROR_MESSAGE,
  identifier = DEFAULT_IDENTIFIER,
}) => {
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null,
  )
  const [showRequiredMessage, setShowRequiredMessage] = useState<boolean>(false)

  // Error Analytics
  useEffect(() => {
    if (showRequiredMessage) allytm.event('customError', errorMessage)
  }, [showRequiredMessage])

  const handleSubmit = (): void => {
    if (!selectedAccountId) {
      setShowRequiredMessage(true)
      return
    }
    const selectedAccount = accounts.find(
      account => account[identifier] === selectedAccountId,
    )
    if (!selectedAccount) return

    onSubmit(selectedAccountId)
  }

  return (
    <div>
      <Space marginBottom="24px">
        <TextHeading tag="h3" size="md">
          {title}
        </TextHeading>
      </Space>
      {subTitle && (
        <Space marginBottom="24px">
          <TextHeading tag="h5" size="xs">
            {subTitle}
          </TextHeading>
        </Space>
      )}
      <Private>
        <StyledSpace>
          <StyledLabel htmlFor="card-selector" id="card-selector-label">
            {labelText}
          </StyledLabel>
          <CardSelectionGroup
            errorNotification={{
              message: showRequiredMessage ? errorMessage : undefined,
            }}
            align="stacked"
            ariaLabelledby="account-selector-label"
            ariaRequired
            id="account-selector"
            variant="single-select"
            onSelection={(accountId: string | undefined): void => {
              setSelectedAccountId(accountId ?? null)
              if (accountId) setShowRequiredMessage(false)
            }}
          >
            {accounts.map(account => (
              <CardSelectionCard
                cardId={account[identifier]}
                title={`${account.nickName ?? account.name} ${maskAccountNumber(
                  account.accountNumber,
                )}`}
              />
            ))}
          </CardSelectionGroup>
        </StyledSpace>
      </Private>
      <Space marginY="25px">
        <ButtonGroup>
          <Button
            onClick={(): void => handleSubmit()}
            variant="primary"
            text="Continue"
          />
          <MUILink variant="link" text="Go to Snapshot" allytmln="goToSnapshot">
            <Link to="/dashboard" />
          </MUILink>
        </ButtonGroup>
      </Space>
    </div>
  )
}

export default AccountSelector
